import { type CardDetailsWidgetProps } from '@fruitful/types';
import React from 'react';
import { BodyMediumRegular, LoadingSkeleton, Shadows, YStack } from 'smoothie';

import { type DetailsTileAppearance } from '../../../banking/components/details-tile/DetailsTile';
import { DetailsTileForCard } from '../../../banking/components/details-tile/DetailsTileForCard';
import useGetAllBankAccountDetails from '../../../banking/hooks/useGetAllBankAccountDetails';
import { type BankAccountDisplayDetail } from '../../../banking/types/BankAccount';
import { type Card, CardStatus } from '../../../banking/types/Card';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { getNonTerminatedCardsForUser } from '../../../common/utils/cardUtils';

const SKELETON_HEIGHT = 116;

// `maskBackgroundColor` must be opaque (it covers the secure CardData layer
// when details are hidden). Since the tile's `rgba(255,255,255,0.5)` is
// translucent, we use a solid approximation of white composited over the chat
// gradient (#DAEDE4 -> #B4D0D8) so the masked cells blend into the tile
// instead of reading as bright boxes.
const CARD_TILE_APPEARANCE: DetailsTileAppearance = {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderColor: '$white',
    borderRadius: 24,
    rowGap: 40,
    shadow: Shadows.SMALL,
    fullWidth: true,
    maskBackgroundColor: '#E3EFEF',
};

interface ResolvedCard {
    account?: BankAccountDisplayDetail;
    card?: Card;
}

/**
 * The agent identifies the account or card the member asked about. Account
 * selection is preferred because the client can safely choose the signed-in
 * member's card from a joint account. Only the member's own non-terminated
 * cards are eligible; an unresolved selection returns no card instead of
 * silently showing an unrelated default.
 */
const resolveCard = (
    accounts: BankAccountDisplayDetail[],
    syncteraPersonId: string,
    bankAccountId?: string,
    cardId?: string,
): ResolvedCard => {
    const accountsWithCards = accounts
        .map((account) => ({
            account,
            cards: getNonTerminatedCardsForUser(
                syncteraPersonId,
                account.cards,
            ),
        }))
        .filter(({ cards }) => cards.length > 0);

    const isActive = (card: Card) => card.cardStatus === CardStatus.ACTIVE;
    const selected =
        bankAccountId != null
            ? accountsWithCards.find(
                  ({ account }) => account.bankAccountId === bankAccountId,
              )
            : accountsWithCards.find(({ cards }) =>
                  cards.some((card) => card.id === cardId),
              );

    return {
        account: selected?.account,
        card:
            selected?.cards.find((card) => card.id === cardId) ??
            selected?.cards.find(isActive) ??
            selected?.cards[0],
    };
};

export const CardDetailsWidget = ({
    bankAccountId,
    cardId,
}: CardDetailsWidgetProps) => {
    const { syncteraPersonId } = useAuthedUser();
    const { bankAccountDetails, isLoading } = useGetAllBankAccountDetails(true);

    const { account, card } = resolveCard(
        bankAccountDetails,
        syncteraPersonId,
        bankAccountId,
        cardId,
    );

    if (isLoading) {
        return (
            <LoadingSkeleton
                width="100%"
                height={SKELETON_HEIGHT}
                borderRadius={24}
            />
        );
    }

    if (card == null) {
        return (
            <YStack width="100%">
                <BodyMediumRegular color="$gray1000">
                    No card found.
                </BodyMediumRegular>
            </YStack>
        );
    }

    const nickname = account?.nickname;

    return (
        <DetailsTileForCard
            card={card}
            accountName={
                nickname != null && nickname !== ''
                    ? `${nickname} Card`
                    : undefined
            }
            appearance={CARD_TILE_APPEARANCE}
        />
    );
};
